import * as React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';

import { TempChartBtn } from './temp_chart_btn';

interface ChildProps {
  selectedTab: string;
}

interface TemperatureDate {
  meta: { legend: string[] };
  data: [string, number, number, number, number][];
}

export const TempChart: React.FC<ChildProps> = ({ selectedTab }): React.ReactElement => {
  const defaultChart = { id: '24h', name: '24h' };
  const configs = [
    { id: '3h', name: '3h' },
    defaultChart,
    { id: '48h', name: '48h' },
    { id: '1w', name: 'Nädal' },
    { id: '1m', name: 'Kuu' },
    { id: '3m', name: '3 Kuud' },
    { id: '1y', name: 'Aasta' },
  ];

  const [currentChart, setChart] = React.useState(defaultChart.id);
  React.useEffect(() => {
    updateSeries(currentChart).catch((error) => console.log(`Failed to update chart: ${error}`));
  }, [currentChart, selectedTab]);
  const chartButtons = configs.map((config) => (
    <TempChartBtn
      isActive={currentChart === config.id}
      type={config.id}
      name={config.name}
      showChart={(type) => {
        setChart(type);
      }}
    />
  ));
  let seriesStaticConfig: Highcharts.Options = {
    chart: {
      type: 'spline',
    },
    colors: ['#4572A7', '#AA4643', '#89A54E', '#80699B', '#3D96AE', '#DB843D', '#92A8CD', '#A47D7C', '#B5CA92'],
    xAxis: {
      type: 'datetime',
      dateTimeLabelFormats: {
        hour: '%H. %M',
      },
    },
    yAxis: {
      title: {
        text: 'T (°C)',
      },
    },
    plotOptions: {
      series: {
        marker: {
          radius: 2,
        },
      },
    },
    time: {
      timezone: undefined, // the browser's
    },
  };
  const [chartOptions, setChartOptions] = React.useState({
    ...seriesStaticConfig,
    series: [
      {
        data: [[1, 1]],
      },
    ],
  });

  const updateSeries = async (type: string) => {
    const data = await fetch(getDataURL(selectedTab, type));
    const dataJSON: TemperatureDate = JSON.parse(await data.text());
    const series: { data: [number, number][]; name: string }[] = [];
    for (let i = 0; i < dataJSON.meta.legend.length; i++) {
      series.push({
        name: dataJSON.meta.legend[i],
        data: dataJSON.data.map((dataFrame) => [parseInt(dataFrame[0]) * 1000, dataFrame[i + 1] as number]),
      });
    }

    setChartOptions({
      ...seriesStaticConfig,
      title: {
        text: getChartTitle(type),
      },
      series,
    });
  };

  return (
    <div className="row container">
      <div className="col-md-4">
        <div className="row temp-menu">{chartButtons}</div>
      </div>
      <div className="col-md-8">
        <div id="chart">
          <HighchartsReact highcharts={Highcharts} options={chartOptions} />
        </div>
      </div>
    </div>
  );
};

export function getDataURL(selectedTab: string, chartType: string) {
  const subdomain = window.location.host.split('.')[1] ? window.location.host.split('.')[0] : false;
  if (subdomain === 'ingliste') {
    return `data/ingliste_${selectedTab.replace('#', '')}_temperature${chartType}.json`;
  }
  return `data/${selectedTab.replace('#', '')}_temperature${chartType}.json`;
}

function getChartTitle(chartType: string) {
  switch (chartType) {
    case '3h':
      return 'Viimased 3 tundi';
    case '48h':
      return 'Viimased 48 tundi';
    case '1w':
      return 'Viimane nädal';
    case '1m':
      return 'Viimane kuu';
    case '3m':
      return 'Viimased 3 kuud';
    case '1y':
      return 'Viimane aasta';
    case '1yminmax':
      return 'Viimase aasta miinimum-maksimum';
    default:
      return 'Viimased 24 tundi';
  }
}
